import { html, unsafeCSS, type CSSResultGroup } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LibElement } from '../lib-element';
import css from './lib-flyout.css';

const openFlyouts = new Set<LibFlyout>();
const escapeOwners = new WeakMap<KeyboardEvent, Set<LibFlyout>>();

@customElement('lib-flyout')
export class LibFlyout extends LibElement {
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(css)];
  @property({ type: Boolean }) open = false;
  @property() alignment = 'end';
  @property({ attribute: 'auto-position', converter: v => v !== 'false' }) autoPosition = true;
  @property({ type: Boolean, attribute: 'custom-surface' }) customSurface = false;
  @property({ reflect: true }) surface = 'white';
  @property({ attribute: 'close-on-escape', converter: v => v !== 'false' }) closeOnEscape = true;
  @property({ attribute: 'close-on-outside-click', converter: v => v !== 'false' }) closeOnOutsideClick = true;
  private readonly ancestors = new MutationObserver(() => this.resetClosedAncestor());
  private resetClosedAncestor(): boolean {
    if (!this.closedAncestor()) return false;
    if (this.open) {
      this.open = false;
      this.stop(false);
      this.emit('lib-flyout-ancestor-close');
    }
    return true;
  }
  private wasOpen = false;
  private focused: HTMLElement | null = null;
  private frame = 0;
  private dismissPending = false;
  private readonly resizeObserver = new ResizeObserver(() => this.schedule());
  override updated() {
    if (this.closedAncestor()) this.open = false;
    this.classList.toggle('lib-flyout-start', this.alignment === 'start');
    this.classList.toggle('lib-flyout-custom-surface', this.customSurface);
    this.classList.toggle('lib-flyout-open', this.open);
    if (this.open && !this.wasOpen) {
      this.syncPopover();
      openFlyouts.add(this);
      this.dismissPending = false;
      this.focused = this.activeElement();
      document.addEventListener('pointerdown', this.pointer, true);
      document.addEventListener('keydown', this.key);
      document.addEventListener('scroll', this.schedule, true);
      window.addEventListener('resize', this.schedule);
      this.addEventListener('animationend', this.scheduleChildren);
      this.resizeObserver.observe(this);
      if (this.parentElement) this.resizeObserver.observe(this.parentElement);
    }
    if (this.open) this.position();
    this.classList.toggle('lib-flyout-open', this.open);
    if (!this.open && this.wasOpen) {
      this.syncPopover();
      for (const child of openFlyouts) {
        if (child !== this && this.composedContains(child)) {
          child.open = false;
          child.stop(false);
        }
      }
      this.stop();
    }
    this.wasOpen = this.open;
  }
  private syncPopover(): void {
    const element = this as HTMLElement & { showPopover?: () => void; hidePopover?: () => void; };
    if (!this.hasAttribute('popover') || typeof element.showPopover !== 'function' || typeof element.hidePopover !== 'function') return;
    const visible = this.isPopoverOpen();
    if (this.open && !visible) element.showPopover();
    if (!this.open && visible) element.hidePopover();
  }
  private isPopoverOpen(): boolean {
    const element = this as HTMLElement & { showPopover?: () => void; hidePopover?: () => void; };
    return this.hasAttribute('popover') && typeof element.showPopover === 'function' && typeof element.hidePopover === 'function'
      && this.matches(':popover-open');
  }
  private position() {
    const anchor = this.parentElement;
    if (!anchor) return;
    const topLayer = this.isPopoverOpen();
    this.classList.remove('lib-flyout-fixed');
    this.style.removeProperty('--lib-flyout-top');
    this.style.removeProperty('--lib-flyout-left');
    let shift = 0, up = false, fixed = topLayer;
    if (this.autoPosition) {
      const a = anchor.getBoundingClientRect(), p = this.getBoundingClientRect();
      const { clip, clipped } = this.clippingBounds(anchor, topLayer);
      fixed = topLayer || clipped;
      const below = Math.max(0, Math.round(clip.bottom - a.bottom)), above = Math.max(0, Math.round(a.top - clip.top));
      up = below < p.height && (above >= p.height || above > below);
      const start = this.alignment === 'start';
      const available = Math.max(0, Math.round(start ? clip.right - a.right : a.left - clip.left)) + Math.round(a.width);
      if (p.width > available) shift = (start ? -1 : 1) * (Math.round(p.width) - available);
      if (fixed) this.setFixedPosition(a, p, up, shift, topLayer);
    }
    this.classList.toggle('lib-flyout-fixed', fixed);
    this.classList.toggle('lib-flyout-up', up);
    this.style.setProperty('--lib-flyout-shift-x', `${shift}px`);
    this.scheduleChildren();
  }
  private setFixedPosition(anchor: DOMRect, panel: DOMRect, up: boolean, shift: number, topLayer: boolean): void {
    const requestedTop = up ? anchor.top - panel.height : anchor.bottom;
    const top = topLayer
      ? Math.min(Math.max(0, requestedTop), Math.max(0, innerHeight - panel.height))
      : requestedTop;
    const left = this.alignment === 'start' ? anchor.left + shift : anchor.right - panel.width + shift;
    this.style.setProperty('--lib-flyout-top', `${top}px`);
    this.style.setProperty('--lib-flyout-left', `${left}px`);
  }
  private clippingBounds(anchor: Element, topLayer: boolean) {
    const clip = { top: 0, bottom: innerHeight, left: 0, right: innerWidth };
    let vertical = false, horizontal = false;
    if (topLayer) return { clip, clipped: false };
    for (let ancestor = this.composedParent(anchor); ancestor && ancestor !== document.body; ancestor = this.composedParent(ancestor)) {
      const style = getComputedStyle(ancestor), rect = ancestor.getBoundingClientRect();
      if (!vertical && /(auto|scroll|hidden|clip)/.test(style.overflowY)) {
        clip.top = Math.max(0, rect.top);
        clip.bottom = Math.min(innerHeight, rect.bottom);
        vertical = true;
      }
      if (!horizontal && /(auto|scroll|hidden|clip)/.test(style.overflowX)) {
        clip.left = Math.max(0, rect.left);
        clip.right = Math.min(innerWidth, rect.right);
        horizontal = true;
      }
    }
    return { clip, clipped: vertical || horizontal };
  }
  private readonly scheduleChildren = () => {
    for (const child of openFlyouts) {
      if (child !== this && this.composedContains(child)) child.schedule();
    }
  };
  private readonly schedule = () => {
    if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; if (this.open) this.position(); });
  };
  private readonly key = (e: KeyboardEvent) => {
    if (this.resetClosedAncestor() || e.key !== 'Escape') return;
    let owners = escapeOwners.get(e);
    if (!owners) {
      if (e.defaultPrevented) return;
      owners = new Set([...openFlyouts].filter(panel =>
        ![...openFlyouts].some(child => child !== panel && panel.composedContains(child))));
      escapeOwners.set(e, owners);
    }
    if (!owners.has(this)) return;
    if (this.closeOnEscape || [...openFlyouts].some(parent => parent !== this && parent.composedContains(this))) {
      e.preventDefault();
    }
    if (!e.repeat && this.closeOnEscape) this.dismiss();
  };
  private dismiss() {
    if (this.dismissPending) return;
    this.dismissPending = true;
    this.emit('lib-flyout-dismiss');
  }
  private readonly pointer = (e: PointerEvent) => {
    if (this.resetClosedAncestor() || !this.closeOnOutsideClick || e.composedPath().includes(this)) return;
    if (!e.composedPath().some(node => node instanceof Element &&
      ((node instanceof HTMLElement && node.dataset.libFlyoutAnchor === this.id) || node.getAttribute('aria-controls') === this.id))) this.dismiss();
  };
  private composedParent(element: Element): Element | null {
    return element.assignedSlot ?? element.parentElement ?? (element.getRootNode() instanceof ShadowRoot ? (element.getRootNode() as ShadowRoot).host : null);
  }
  private activeElement(): HTMLElement | null {
    let active = document.activeElement;
    while (active?.shadowRoot?.activeElement) active = active.shadowRoot.activeElement;
    return active instanceof HTMLElement ? active : null;
  }
  private composedContains(element: Element): boolean {
    for (let current: Element | null = element; current; current = this.composedParent(current)) {
      if (current === this) return true;
    }
    return false;
  }
  private closedAncestor(): boolean {
    for (let parent = this.composedParent(this); parent; parent = this.composedParent(parent)) {
      if (parent instanceof HTMLDialogElement && !parent.open) return true;
      if (parent instanceof LibFlyout && !parent.open) return true;
    }
    return false;
  }
  private stop(restoreFocus = true) {
    openFlyouts.delete(this);
    document.removeEventListener('pointerdown', this.pointer, true);
    document.removeEventListener('keydown', this.key);
    document.removeEventListener('scroll', this.schedule, true);
    window.removeEventListener('resize', this.schedule);
    this.removeEventListener('animationend', this.scheduleChildren);
    this.resizeObserver.disconnect();
    cancelAnimationFrame(this.frame); this.frame = 0;
    const active = this.activeElement();
    if (restoreFocus && active && this.composedContains(active) && this.focused?.isConnected &&
      !this.closedAncestor()) {
      let target = this.focused;
      while (target.shadowRoot?.activeElement instanceof HTMLElement) target = target.shadowRoot.activeElement;
      const button = target.shadowRoot?.querySelector('button');
      (button ?? target).focus({ preventScroll: true });
    }
  }
  override connectedCallback() {
    super.connectedCallback();
    for (let parent = this.composedParent(this); parent; parent = this.composedParent(parent)) {
      if (parent instanceof HTMLDialogElement) this.ancestors.observe(parent, { attributes: true, attributeFilter: ['open'] });
    }
    this.requestUpdate();
  }
  override disconnectedCallback() {
    this.ancestors.disconnect();
    for (const child of openFlyouts) {
      if (child !== this && this.composedContains(child)) {
        child.open = false;
        child.stop(false);
      }
    }
    this.stop(false); this.wasOpen = false; super.disconnectedCallback();
  }
  override render() { return html`<slot name="close"></slot><slot></slot>`; }
}
interface FlyoutReference { invokeMethodAsync(method: string): Promise<unknown>; }
const bindings = new WeakMap<LibFlyout, EventListener>();
export function bindFlyout(element: LibFlyout | null, reference: FlyoutReference) {
  if (!element) return;
  unbindFlyout(element);
  const dismiss = (event: Event) => {
    if (event.composedPath()[0] === element) void reference.invokeMethodAsync(event.type === 'lib-flyout-ancestor-close' ? 'ResetFromAncestorAsync' : 'CloseAsync');
  };
  bindings.set(element, dismiss); element.addEventListener('lib-flyout-dismiss', dismiss); element.addEventListener('lib-flyout-ancestor-close', dismiss);
}
export function unbindFlyout(element: LibFlyout | null) {
  if (!element) return;
  const dismiss = bindings.get(element);
  if (dismiss) { element.removeEventListener('lib-flyout-dismiss', dismiss); element.removeEventListener('lib-flyout-ancestor-close', dismiss); }
  bindings.delete(element);
}
declare global { interface HTMLElementTagNameMap { 'lib-flyout': LibFlyout; } }
